import axios, { AxiosError } from 'axios';
import { apiClient, apiClientFormEncoded } from './client';
import { useGuardianAuthStore } from '../../stores/guardianAuthStore';
import {
  Guardian,
  GuardianTokenResponse,
  GuardianChild,
  GuardianObservation,
  GuardianAssessment,
  GuardianInvitation,
  GuardianInvitationAccept,
} from '../../types/api';

// Guardian-specific API client that reads from the guardian auth store
const guardianApiClient = axios.create({
  baseURL: apiClient.defaults.baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor: inject guardian token from guardianAuthStore
guardianApiClient.interceptors.request.use(
  (config) => {
    const token = useGuardianAuthStore.getState().accessToken;
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: handle 401 with guardian token refresh
guardianApiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && originalRequest) {
      // Skip refresh for auth endpoints
      if (
        originalRequest.url?.includes('/guardians/auth/login') ||
        originalRequest.url?.includes('/guardians/auth/refresh')
      ) {
        return Promise.reject(error);
      }

      // Prevent infinite retry loops
      if ((originalRequest as any)._retry) {
        useGuardianAuthStore.getState().clearAuth();
        if (typeof window !== 'undefined' && window.location && !import.meta.env.TEST) {
          window.location.href = '/guardian/login';
        }
        return Promise.reject(error);
      }

      (originalRequest as any)._retry = true;

      try {
        await useGuardianAuthStore.getState().refreshAccessToken();
        const newToken = useGuardianAuthStore.getState().accessToken;
        if (newToken && originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }
        return guardianApiClient(originalRequest);
      } catch (refreshError) {
        useGuardianAuthStore.getState().clearAuth();
        if (typeof window !== 'undefined' && window.location && !import.meta.env.TEST) {
          window.location.href = '/guardian/login';
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export { guardianApiClient };

export const guardianApi = {
  // Authentication endpoints (use shared clients - no guardian token needed yet)
  auth: {
    login: async (username: string, password: string, organizationCode?: string): Promise<GuardianTokenResponse> => {
      const formData = new URLSearchParams();
      formData.append('username', username);
      formData.append('password', password);

      const loginUrl = organizationCode
        ? `/guardians/auth/login?organization_code=${encodeURIComponent(organizationCode)}`
        : '/guardians/auth/login';

      const response = await apiClientFormEncoded.post<GuardianTokenResponse>(
        loginUrl,
        formData
      );
      return response.data;
    },

    logout: async (): Promise<void> => {
      await guardianApiClient.post('/guardians/auth/logout');
    },

    requestPasswordReset: async (email: string): Promise<{ message: string }> => {
      const response = await apiClient.post<{ message: string }>(
        '/auth/guardian/password-reset/request',
        { email }
      );
      return response.data;
    },

    confirmPasswordReset: async (
      token: string,
      newPassword: string
    ): Promise<{ message: string }> => {
      const response = await apiClient.post<{ message: string }>(
        '/auth/guardian/password-reset/confirm',
        { token, new_password: newPassword }
      );
      return response.data;
    },

    refreshToken: async (refreshToken: string): Promise<GuardianTokenResponse> => {
      const response = await guardianApiClient.post<GuardianTokenResponse>(
        '/guardians/auth/refresh',
        { refresh_token: refreshToken }
      );
      return response.data;
    },
  },

  // Portal endpoints - use guardianApiClient (reads guardian auth token)
  portal: {
    getProfile: async (): Promise<Guardian> => {
      const response = await guardianApiClient.get<Guardian>('/guardians/portal/me/profile');
      return response.data;
    },

    updateProfile: async (data: Partial<Guardian>): Promise<Guardian> => {
      const response = await guardianApiClient.patch<Guardian>(
        '/guardians/portal/me/profile',
        data
      );
      return response.data;
    },

    getChildren: async (): Promise<GuardianChild[]> => {
      const response = await guardianApiClient.get<GuardianChild[]>(
        '/guardians/portal/me/children'
      );
      return response.data;
    },

    getChild: async (childId: string): Promise<GuardianChild> => {
      const response = await guardianApiClient.get<GuardianChild>(
        `/guardians/portal/me/children/${childId}`
      );
      return response.data;
    },

    getChildObservations: async (
      childId: string,
      params?: {
        skip?: number;
        limit?: number;
      }
    ): Promise<GuardianObservation[]> => {
      const response = await guardianApiClient.get<GuardianObservation[]>(
        `/guardians/portal/me/children/${childId}/observations`,
        { params }
      );
      return response.data;
    },

    getChildAssessments: async (
      childId: string,
      params?: {
        skip?: number;
        limit?: number;
      }
    ): Promise<GuardianAssessment[]> => {
      const response = await guardianApiClient.get<GuardianAssessment[]>(
        `/guardians/portal/me/children/${childId}/assessments`,
        { params }
      );
      return response.data;
    },
  },

  // Invitation endpoints (no auth needed - token-based)
  invitations: {
    verify: async (token: string): Promise<GuardianInvitation> => {
      const response = await apiClient.get<GuardianInvitation>(
        `/guardians/invitations/verify/${token}`
      );
      return response.data;
    },

    accept: async (
      token: string,
      data: GuardianInvitationAccept
    ): Promise<Guardian> => {
      const response = await apiClient.post<Guardian>(
        `/guardians/invitations/accept/${token}`,
        data
      );
      return response.data;
    },

    resend: async (email: string): Promise<{ message: string }> => {
      const response = await apiClient.post<{ message: string }>(
        '/guardians/invitations/resend',
        { email }
      );
      return response.data;
    },
  },
};